import React, { useEffect, useState } from 'react';
import { Policy } from '../../types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { deletePolicy, fetchPolicies, fetchPolicyAdmin } from '../../features/admin/policySlice';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import ConfirmDeleteModal from './models/ConfirmDeleteModal';
import InnerLoading from '../ui/InnerLoading';
import PolicyModal from './models/PolicyModal';


const PolicyPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { policies, loading } = useSelector((state: RootState) => state.policySlice);

    const [modalOpen, setModalOpen] = useState(false);
    const [editPolicy, setEditPolicy] = useState<Policy | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedPolicyId, setSelectedPolicyId] = useState<number | null>(null);

    useEffect(() => {
        dispatch(fetchPolicies());
    }, [dispatch]);

    const handleDelete = async () => {
        if (selectedPolicyId !== null) {
            await dispatch(deletePolicy(selectedPolicyId));
            dispatch(fetchPolicies());
            setSelectedPolicyId(null);
        }
    };
    const handleEdit = (policyData: Policy) => {
        dispatch(fetchPolicyAdmin(policyData.id))
            .unwrap()
            .then((data) => {
                setEditPolicy(data);
                setModalOpen(true);
            })
    };
    const handleAdd = () => {
        setEditPolicy(null);
        setModalOpen(true);
    };

    const confirmDelete = (id: number) => {
        setSelectedPolicyId(id);
        setDeleteModalOpen(true);

    };

    return (
        <div >
            <div className='bg-gradient-to-r from-blue-600 to-indigo-500 text-white rounded-xl shadow-md p-6 mb-6'>
                <h2 className='text-white text-lg font-semibold'>{t("policy-title")}</h2>
            </div>

            {loading ? (
                <InnerLoading />
            ) : policies.length === 0 ? (
                <p className="text-center text-gray-500">{t("no-policies")}</p>
            ) : (
                <ul className='space-y-5'>
                    {policies.map((policy) => (
                        <li key={policy.id} className="border bg-white rounded-xl p-4 shadow space-y-2">
                            <p className="text-gray-700">{policy.details}</p>
                            {policy.link && (
                                <a href={policy.link.startsWith('http') ? policy.link : `https://${policy.link}`} className="text-blue-500 underline">
                                    {t("view-link")}
                                </a>
                            )}
                            <div className="flex justify-self-end gap-3 mt-3">
                                <button
                                    onClick={() => handleEdit(policy)}
                                    className="p-2 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-full transition"
                                    title={t("edit")}
                                >
                                    <FiEdit size={18} />
                                </button>
                                <button
                                    onClick={() => policy.id && confirmDelete(policy.id)}
                                    className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-full transition"
                                    title={t("delete")}
                                >
                                    <FiTrash2 size={18} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <div className="flex justify-end w-full pt-6">
                <button
                    onClick={handleAdd}
                    className="bg-primary px-6 py-2 rounded-full text-white hover:bg-darkColor transition">
                    + {t("add-new-policy")}
                </button>
            </div>

            {modalOpen && (
                <PolicyModal
                    onClose={() => setModalOpen(false)}
                    onSuccess={() => dispatch(fetchPolicies())}
                    initialData={editPolicy}
                />
            )}

            <ConfirmDeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleDelete}
                messageKey="delete-policy-confirm"
            />
        </div>
    );
};
export default PolicyPage;
